/**
 * License Guard
 *
 * Wraps the entire app. On load it asks the backend for license status:
 *   - enforced && !activated  → render the ActivationScreen (hard lock).
 *   - otherwise               → render the app normally.
 *
 * If the status endpoint is unreachable (e.g. backend still starting) we fail
 * OPEN — the app renders and normal API error handling applies — so a transient
 * network blip never traps the operator on a license wall.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Key, Copy, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { getLicenseStatus, activateLicense, type LicenseStatus } from '@/services/license';

function FullScreenSpinner({ label }: { label: string }) {
  return (
    <div className="h-screen bg-cosmic-mesh flex items-center justify-center text-slate-800 antialiased overflow-hidden">
      <div className="mesh-light" />
      <div className="text-center space-y-3">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#1565c0] mx-auto" />
        <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">{label}</p>
      </div>
    </div>
  );
}

function ActivationScreen({
  status,
  onActivated,
}: {
  status: LicenseStatus;
  onActivated: () => void | Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const installId = status.install_id ?? 'unknown';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(installId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard not available — operator can select manually */
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await activateLicense(code);
      if (result.ok) {
        await onActivated();
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Activation failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen bg-cosmic-mesh flex items-center justify-center text-slate-800 antialiased overflow-hidden p-4">
      <div className="mesh-light" />

      <div className="glass-card-premium w-full max-w-[480px] p-10 relative overflow-hidden flex flex-col items-center animate-scale-in">
        {/* Top gradient accent bar */}
        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600" />

        {/* Lock badge */}
        <div className="mb-6 relative">
          <div className="h-16 w-16 rounded-2xl bg-slate-900 flex items-center justify-center shadow-xl">
            <Key size={30} weight="bold" className="text-white" />
          </div>
        </div>

        <h1 className="text-xl font-black text-slate-900 dark:text-slate-100 mb-1.5 text-center">
          Activate this installation
        </h1>
        <p className="text-xs font-bold text-slate-500 text-center mb-7 leading-relaxed">
          This copy of Fluent is not activated yet. Send the Install ID below to your
          vendor to receive an activation code.
        </p>

        {/* Install ID block */}
        <div className="w-full mb-6">
          <label className="input-label">Install ID</label>
          <div className="flex items-stretch gap-2">
            <code className="flex-1 bg-slate-100 dark:bg-slate-800/60 border-2 border-slate-200 dark:border-slate-700 rounded-2xl px-4 py-3 text-xs font-mono text-slate-700 dark:text-slate-300 break-all select-all">
              {installId}
            </code>
            <button
              type="button"
              onClick={handleCopy}
              title="Copy Install ID"
              className="shrink-0 px-4 rounded-2xl border-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all flex items-center justify-center"
            >
              {copied ? (
                <CheckCircle size={20} weight="bold" className="text-green-600" />
              ) : (
                <Copy size={20} weight="bold" className="text-slate-500" />
              )}
            </button>
          </div>
        </div>

        {/* Activation form */}
        <form onSubmit={handleSubmit} className="w-full space-y-4">
          <div>
            <label htmlFor="activation-code" className="input-label">
              Activation Code
            </label>
            <textarea
              id="activation-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              rows={4}
              className="input-field font-mono text-xs leading-relaxed resize-none"
              placeholder="Paste the activation code from your vendor here…"
              spellCheck={false}
              autoComplete="off"
            />
          </div>

          {error && (
            <div className="bg-red-50 border-2 border-red-200 dark:border-red-500/30 rounded-2xl p-4">
              <p className="text-red-600 text-xs font-bold text-center">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !code.trim()}
            className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-2xl shadow-xl shadow-slate-900/20 dark:shadow-black/40 transition-all flex items-center justify-center gap-2 group transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                Activating…
              </>
            ) : (
              <>
                Activate
                <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 w-full text-center border-t border-slate-200">
          <p className="text-[10px] font-bold text-slate-400 tracking-tight">
            Contact your vendor with the Install ID to obtain an activation code.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LicenseGuard({ children }: { children: React.ReactNode }) {
  const [status, setStatus] = useState<LicenseStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [checkFailed, setCheckFailed] = useState(false);

  const check = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getLicenseStatus();
      setStatus(s);
      setCheckFailed(false);
    } catch {
      // Backend unreachable / still starting → fail open.
      setCheckFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  if (loading) {
    return <FullScreenSpinner label="Checking activation…" />;
  }

  if (!checkFailed && status && status.enforced && !status.activated) {
    return <ActivationScreen status={status} onActivated={check} />;
  }

  return <>{children}</>;
}
